import { Controller } from "@hotwired/stimulus"

/**
 * Department Selector Controller
 * 부서 선택 모달 컨트롤러
 *
 * 기능:
 * - 부서 선택 모달 열기/닫기
 * - 조직도에서 부서 선택 시 하이라이트 표시
 * - [선택] 버튼 클릭 시 폼 필드 업데이트 및 제출
 */
export default class extends Controller {
  static targets = ["modal", "display", "input", "trigger", "recentChip"]
  static values = {
    allLabel: { type: String, default: "전체" },
    autoSubmit: { type: Boolean, default: true },
    leafOnly: { type: Boolean, default: false },
    multiSelect: { type: Boolean, default: false },
    excludeCode: { type: String, default: "" },
    autofilled: { type: Boolean, default: false },
    // declare 모드: 선택 확정 즉시 서버에 "작업 부서 선언" (PATCH /workplace/working_department).
    // 등록 화면처럼 선택이 곧 작업 맥락인 곳에서만 켠다. 필터 화면('전체'·다중 선택)은 끔.
    declare: { type: Boolean, default: false }
  }

  connect() {
    // org-chart의 nodeClick/nodeDeselect 이벤트 리스닝
    this.handleNodeClick = this.handleNodeClick.bind(this)
    this.handleNodeDeselect = this.handleNodeDeselect.bind(this)
    this.handleConfirm = this.handleConfirm.bind(this)

    // 임시 선택 상태 (single-select)
    this.selectedCode = null
    this.selectedName = null

    // 임시 선택 상태 (multi-select)
    this.selectedCodes = new Set()
    this.selectedNames = new Map()

    // 직전 부서가 자동으로 채워진 상태면 첫 진입 시 한 번 pulse 발화
    if (this.autofilledValue && this.hasTriggerTarget) {
      this.triggerAutofillPulse()
    }
  }

  /**
   * 자동 채움 cue — 첫 진입 시 부서 칸 배경이 녹색으로 살짝 떠올랐다 사라짐.
   * "이건 자동으로 채워진 값이야"를 무의식 수준에서 알려주는 micro-animation.
   */
  triggerAutofillPulse() {
    const el = this.triggerTarget
    // 옆 "제품사진 올리기" 버튼과 같은 색조 (--success-700: #2E7D32 = rgb(46, 125, 50)).
    // 진한 녹색으로 시작 → 0.15s 유지 → 2.5초에 걸쳐 천천히 옅어짐.
    el.animate(
      [
        { backgroundColor: "rgba(46, 125, 50, 0.55)" },
        { backgroundColor: "rgba(46, 125, 50, 0.55)", offset: 0.15 },
        { backgroundColor: "rgba(46, 125, 50, 0)" }
      ],
      { duration: 2500, easing: "ease-out", fill: "none" }
    )
  }

  /**
   * 모달 열기
   */
  openModal(event) {
    event.preventDefault()

    // 임시 선택 상태 초기화 (hidden input은 건드리지 않음 — auto-submit 방지)
    this.selectedCode = null
    this.selectedName = null
    this.selectedCodes.clear()
    this.selectedNames.clear()

    if (this.hasModalTarget) {
      // modal_controller의 open 메서드 호출
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.modalTarget,
        "modal"
      )
      if (modalController) {
        modalController.open()
      }

      // 조직도 선택 하이라이트 초기화
      const orgChartController = this.application.getControllerForElementAndIdentifier(
        this.modalTarget.querySelector('[data-controller~="org-chart"]'),
        "org-chart"
      )
      if (orgChartController) {
        orgChartController.deselectNode()
      }

      // multi-select 모드: 모달 내 모든 선택 표시 제거 (wrapper 기준)
      if (this.multiSelectValue) {
        this.modalTarget.querySelectorAll('[data-node-id].org-node-selected').forEach(el => {
          el.classList.remove('org-node-selected')
        })
      }

      // leafOnly 모드: 선택 불가 노드에 비활성 스타일 적용 + confirm 버튼 비활성화
      if (this.leafOnlyValue) {
        this.modalTarget.querySelectorAll('[data-node-leaf="false"]').forEach(node => {
          const orgNode = node.querySelector('.org-node')
          if (orgNode) {
            orgNode.classList.add('!opacity-40', '!cursor-not-allowed', '!shadow-none')
            orgNode.classList.remove('cursor-pointer', 'hover:shadow-md')
          }
        })

        // 부서 선택 전까지 confirm 버튼 비활성화
        this.setConfirmButtonDisabled(true)
      }

      // excludeCode가 있으면 해당 노드도 비활성화 (자기 자신 부서)
      if (this.excludeCodeValue) {
        const excludeNode = this.modalTarget.querySelector(`[data-node-id="${this.excludeCodeValue}"]`)
        const orgNode = excludeNode?.querySelector('.org-node')
        if (orgNode) {
          orgNode.classList.add('!opacity-40', '!cursor-not-allowed', '!shadow-none')
          orgNode.classList.remove('cursor-pointer', 'hover:shadow-md')
        }
      }

      // 이벤트 리스닝 시작
      this.modalTarget.addEventListener("org-chart:nodeClick", this.handleNodeClick)
      this.modalTarget.addEventListener("org-chart:nodeDeselect", this.handleNodeDeselect)
      this.modalTarget.addEventListener("modal:confirm", this.handleConfirm)
    }
  }

  /**
   * 모달의 confirm 버튼 활성화/비활성화
   */
  setConfirmButtonDisabled(disabled) {
    if (!this.hasModalTarget) return
    const modalController = this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "modal"
    )
    if (modalController && modalController.hasConfirmButtonTarget) {
      modalController.confirmButtonTarget.disabled = disabled
    }
  }

  /**
   * 모달 닫기
   */
  closeModal() {
    if (this.hasModalTarget) {
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.modalTarget,
        "modal"
      )
      if (modalController) {
        modalController.close()
      }

      // 이벤트 리스닝 해제
      this.modalTarget.removeEventListener("org-chart:nodeClick", this.handleNodeClick)
      this.modalTarget.removeEventListener("org-chart:nodeDeselect", this.handleNodeDeselect)
      this.modalTarget.removeEventListener("modal:confirm", this.handleConfirm)
    }
  }

  /**
   * 조직도 노드 선택 취소 핸들러
   */
  handleNodeDeselect(event) {
    // multi 모드는 click toggle로만 관리
    if (this.multiSelectValue) return

    this.selectedCode = null
    this.selectedName = null

    // leafOnly 모드: 선택 취소 시 confirm 버튼 비활성화
    if (this.leafOnlyValue) {
      this.setConfirmButtonDisabled(true)
    }
  }

  /**
   * 조직도 노드 클릭 핸들러 - 선택만 표시
   */
  handleNodeClick(event) {
    const { nodeId } = event.detail
    if (!nodeId) return

    // 노드에서 부서명 가져오기
    const node = this.modalTarget.querySelector(`[data-node-id="${nodeId}"]`)
    const nameElement = node?.querySelector('[data-testid^="dept-name-"]')
    const deptName = nameElement?.textContent?.trim() || nodeId

    // leafOnly 모드: 말단 부서만 선택 가능
    if (this.leafOnlyValue) {
      const isLeaf = node?.getAttribute('data-node-leaf') === 'true'
      if (!isLeaf) return
    }

    // excludeCode 일치 시 무시 (자기 자신 부서)
    if (this.excludeCodeValue && nodeId === this.excludeCodeValue) return

    // multi-select 모드: toggle
    if (this.multiSelectValue) {
      // CSS 셀렉터 `.org-node-selected > .org-node` 와 일치시키려면
      // 노드 wrapper(`[data-node-id]`)에 클래스 추가
      if (this.selectedCodes.has(nodeId)) {
        this.selectedCodes.delete(nodeId)
        this.selectedNames.delete(nodeId)
        node?.classList.remove('org-node-selected')
      } else {
        this.selectedCodes.add(nodeId)
        this.selectedNames.set(nodeId, deptName)
        node?.classList.add('org-node-selected')
      }

      // 1개 이상 선택 시 confirm 버튼 활성화
      this.setConfirmButtonDisabled(this.selectedCodes.size === 0)
      return
    }

    // 최상위 노드(depth=0) 클릭 시: leafOnly 모드면 실제 코드, 아니면 "전체"
    const nodeDepth = node?.getAttribute('data-node-depth')
    if (nodeDepth === '0' && !this.leafOnlyValue) {
      this.selectedCode = 'all'
      this.selectedName = this.allLabelValue
    } else {
      this.selectedCode = nodeId
      this.selectedName = deptName
    }

    // leafOnly 모드: 부서 선택 후 confirm 버튼 활성화
    if (this.leafOnlyValue) {
      this.setConfirmButtonDisabled(false)
    }
  }

  /**
   * [선택] 버튼 클릭 핸들러
   */
  handleConfirm(event) {
    event.preventDefault()

    // multi-select 모드: 콤마 구분 codes를 input에 기록
    if (this.multiSelectValue) {
      if (this.selectedCodes.size > 0) {
        const codes = Array.from(this.selectedCodes).join(',')
        const names = Array.from(this.selectedCodes).map(c => this.selectedNames.get(c)).join(', ')
        this.updateSelection(codes, names)
        this.closeModal()
        if (this.autoSubmitValue) {
          this.submitForm()
        }
      } else {
        this.closeModal()
      }
      return
    }

    // 선택된 부서가 있으면 적용
    if (this.selectedCode && this.selectedName) {
      this.updateSelection(this.selectedCode, this.selectedName)
      this.closeModal()
      if (this.autoSubmitValue) {
        this.submitForm()
      }
    } else {
      // 선택된 부서가 없으면 모달만 닫기
      this.closeModal()
    }
  }

  /**
   * 선택값 업데이트
   */
  updateSelection(code, name) {
    // hidden input 업데이트
    if (this.hasInputTarget) {
      this.inputTarget.value = code
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    // 작업 부서 선언 — 선택 즉시 서버 기록. 이후 화면들은 파라미터 전달 없이 서버값을 읽는다.
    if (this.declareValue && code && code !== "all") {
      this.declareWorkingDepartment(code)
    }

    // 표시 텍스트 업데이트 — 사용자 명시 선택이라 placeholder 색 → 본문 색
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = name
      this.displayTarget.classList.remove("text-[var(--gray-500)]")
      this.displayTarget.classList.add("text-[var(--gray-900)]", "truncate")
    }

    // "최근" 칩 제거 — 사용자가 의식적으로 선택했으니 자동 채움 표식 사라짐
    if (this.hasRecentChipTarget) {
      this.recentChipTarget.remove()
    }
  }

  /**
   * 폼 제출 (자동 필터링)
   */
  submitForm() {
    // 가장 가까운 form 찾아서 submit
    const form = this.element.closest("form")
    if (form) {
      form.requestSubmit()
    }
  }

  /**
   * 작업 부서 선언 — 실패해도 화면 동작은 계속(fire-and-forget), 다음 선택에서 재시도된다.
   */
  declareWorkingDepartment(code) {
    fetch("/workplace/working_department", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ department_code: code })
    }).then(res => {
      if (!res.ok) console.warn("[department-selector] 작업 부서 선언 실패:", res.status)
    }).catch(err => {
      console.warn("[department-selector] 작업 부서 선언 실패:", err)
    })
  }
}
